const DEFAULT_DELAY = 450;
const DEFAULT_MOVE_THRESHOLD = 10;

const pointFromTouchEvent = event => {
  const touch = event.touches?.[0] || event.changedTouches?.[0];
  if (!touch) return null;

  return {
    x: touch.pageX ?? touch.clientX,
    y: touch.pageY ?? touch.clientY,
  };
};

/**
 * Creates touch handlers for a deliberate long-press without changing ordinary
 * taps or scroll gestures. The consumer owns menu rendering and positioning.
 */
export function useLongPress({
  onLongPress,
  shouldIgnore = () => false,
  delay = DEFAULT_DELAY,
  moveThreshold = DEFAULT_MOVE_THRESHOLD,
}) {
  let timerId = null;
  let startPoint = null;
  let target = null;
  let didLongPress = false;

  const clear = () => {
    if (timerId !== null) window.clearTimeout(timerId);
    timerId = null;
    startPoint = null;
    target = null;
  };

  const onTouchStart = event => {
    if (event.touches?.length !== 1 || shouldIgnore(event)) return;

    const point = pointFromTouchEvent(event);
    if (!point) return;

    didLongPress = false;
    startPoint = point;
    target = event.target;
    timerId = window.setTimeout(() => {
      timerId = null;
      didLongPress = true;
      onLongPress({ ...startPoint, target });
    }, delay);
  };

  const onTouchMove = event => {
    if (timerId === null || !startPoint) return;

    const point = pointFromTouchEvent(event);
    if (!point) {
      clear();
      return;
    }

    const moved = Math.hypot(point.x - startPoint.x, point.y - startPoint.y);
    if (moved > moveThreshold) clear();
  };

  const releaseCompletedPress = () => {
    // Let the browser finish the current touch event before allowing later
    // mouse/keyboard selections normally.
    window.setTimeout(() => {
      didLongPress = false;
    }, 0);
  };

  const onTouchEnd = () => {
    clear();
    releaseCompletedPress();
  };

  const onTouchCancel = () => {
    clear();
    releaseCompletedPress();
  };

  // iOS may dispatch selectstart as it tries to begin native selection. Limit
  // suppression to a pending or completed custom long-press; mouse selection
  // and every ordinary touch remain untouched.
  const onSelectStart = event => {
    if (timerId !== null || didLongPress) event.preventDefault();
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    onSelectStart,
  };
}
